import type { User } from "@/types"

export type LoginRedirectHint = "student" | "instructor"

/**
 * Validates and sanitizes a redirect path to prevent open-redirect attacks.
 *
 * Only same-origin, relative paths are allowed. Absolute URLs, protocol-relative
 * URLs, and paths containing dangerous sequences are rejected.
 *
 * @param path - The raw redirect path from a query parameter.
 * @returns The sanitized path if safe, or `undefined` if the path is rejected.
 */
export const sanitizeRedirectPath = (path: string | undefined): string | undefined => {
  if (!path) {
    return undefined
  }

  // Reject absolute URLs (http://, https://, or any scheme)
  if (/^[a-z][a-z0-9+.-]*:/i.test(path)) {
    return undefined
  }

  // Reject protocol-relative URLs (//example.com)
  if (path.startsWith("//")) {
    return undefined
  }

  // Reject backslash-based bypasses (browsers may normalize \ to /)
  if (path.includes("\\")) {
    return undefined
  }

  // Must start with a single forward slash (relative to origin)
  if (!path.startsWith("/")) {
    return undefined
  }

  // Reject paths that try to redirect to the login page itself (avoid loops)
  const pathWithoutQuery = path.split("?")[0].split("#")[0]
  if (pathWithoutQuery === "/login" || pathWithoutQuery === "/login/") {
    return undefined
  }

  return path
}

/**
 * Resolves the post-login route based on an explicit redirect path, role hints,
 * and backend user details.
 *
 * @param user - Authenticated user profile returned from the backend.
 * @param redirectHint - Optional role hint from the login UI.
 * @param redirectPath - Optional pre-validated redirect path from the URL.
 * @returns The route to navigate to after authentication.
 */
export const getLoginRedirectPath = (user: User, redirectHint?: LoginRedirectHint, redirectPath?: string): string => {
  // A validated redirect path takes highest priority
  if (redirectPath) {
    return redirectPath
  }

  if (redirectHint === "instructor") {
    return "/instructor/dashboard"
  }

  if (redirectHint === "student") {
    return "/dashboard"
  }

  if (user.type === "instructor") {
    return "/instructor/dashboard"
  }

  if (user.type === "student") {
    return "/dashboard"
  }

  if (user.role === "admin") {
    return "/instructor/dashboard"
  }

  return "/dashboard"
}
